import { useEffect, useRef, useState } from "react";

import { ActionIcon, Button, Group, Stack, Switch, Table, UnstyledButton } from "@mantine/core";

import { IconEdit, IconPlus, IconReceipt, IconTrash } from "@tabler/icons-react";

import EmptyRow from "@/components/EmptyRow";
import { Loading } from "@/components/Loading";
import ProcedureInformationDrawer from "@/components/ProcedureInformationDrawer";
import ScrollableTable from "@/components/ScrollableTable";
import { TextWrapper } from "@/components/TextWrapper";

import { CustomCodeDeleteModal, CustomCodeDrawer, ProcedureTableHeader } from "../components";
import { getColumnConfig, TABLE_MIN_WIDTH } from "../constants/fee-table";
import { useCustomCodes } from "../hooks/useCustomCodes";
import { ProcedureCode } from "../types/fee-table";
import { CustomCodeFormType } from "../zod-schemas/custom-code";

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `$${value.toLocaleString()}`;
}

type PanelType = "drawer" | "info" | "delete" | null;

interface CustomCodesProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
  readOnly?: boolean;
}

export default function CustomCodes({
  refreshKey = 0,
  onLoadingChange,
  readOnly = false,
}: CustomCodesProps) {
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureCode | null>(null);
  const [activePanel, setActivePanel] = useState<PanelType>(null);
  /** Bumps on each "Add Custom Code" so the drawer remounts; avoids fee NumberInputs sticking between back-to-back creates. */
  const [customCodeCreateSession, setCustomCodeCreateSession] = useState(0);

  const { procedures, isLoading, saveCustomCode, removeCustomCode, toggleStatus, isUpdating } =
    useCustomCodes(refreshKey);

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    return () => onLoadingChangeRef.current?.(false);
  }, []);

  const openPanel = (panel: Exclude<PanelType, null>, procedure: ProcedureCode | null) => {
    setSelectedProcedure(procedure);
    setActivePanel(panel);
  };

  const closePanel = () => {
    setActivePanel(null);
    setSelectedProcedure(null);
  };

  const handleCreate = () => {
    setCustomCodeCreateSession((previous) => previous + 1);
    openPanel("drawer", null);
  };

  const handleEdit = (procedure: ProcedureCode) => {
    openPanel("drawer", procedure);
  };

  const handleInfo = (procedure: ProcedureCode) => {
    openPanel("info", procedure);
  };

  const handleDelete = (procedure: ProcedureCode) => {
    openPanel("delete", procedure);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProcedure) return;
    await removeCustomCode(selectedProcedure.code);
    closePanel();
  };

  const handleSave = async (formValues: CustomCodeFormType, existingCode?: string) => {
    await saveCustomCode(formValues, existingCode);
  };

  const handleStatusToggle = (procedure: ProcedureCode, isActive: boolean) => {
    void toggleStatus(procedure.code, isActive);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Stack gap="md">
        <Group justify="flex-end">
          {!readOnly && (
            <Button
              onClick={handleCreate}
              leftSection={<IconPlus size={16} />}
              data-testid="custom-codes-add-button"
            >
              Add Custom Code
            </Button>
          )}
        </Group>
        <ScrollableTable
          miw={TABLE_MIN_WIDTH}
          scrollProps={{ mt: 0, mb: 0 }}
          scrollStyle={{ borderRadius: 0, border: "none" }}
        >
          <ProcedureTableHeader />
          <Table.Tbody>
            {procedures.length === 0 ? (
              <EmptyRow
                Icon={IconReceipt}
                addButtonColor="burgundy.9"
                addButtonLabel="Add Custom Code"
                text="There are no custom codes."
                onAdd={readOnly ? undefined : handleCreate}
              />
            ) : (
              procedures.map((procedure) => (
                <CustomCodeRow
                  readOnly={readOnly}
                  onEdit={handleEdit}
                  onInfo={handleInfo}
                  key={procedure.code}
                  procedure={procedure}
                  onDelete={handleDelete}
                  onStatusToggle={handleStatusToggle}
                  isUpdating={isUpdating(procedure.code)}
                />
              ))
            )}
          </Table.Tbody>
        </ScrollableTable>
      </Stack>
      <CustomCodeDrawer
        onSave={handleSave}
        onClose={closePanel}
        procedure={selectedProcedure}
        existingCustomCodes={procedures}
        opened={activePanel === "drawer"}
        key={
          activePanel === "drawer"
            ? selectedProcedure
              ? `edit-${selectedProcedure.code}`
              : `create-${customCodeCreateSession}`
            : "fee-custom-drawer-idle"
        }
      />
      <ProcedureInformationDrawer
        onClose={closePanel}
        opened={activePanel === "info"}
        title={selectedProcedure?.name}
        description={selectedProcedure?.information}
      />
      <CustomCodeDeleteModal
        onClose={closePanel}
        procedure={selectedProcedure}
        onConfirm={handleDeleteConfirm}
        opened={activePanel === "delete"}
      />
    </>
  );
}

interface CustomCodeRowProps {
  procedure: ProcedureCode;
  onEdit: (procedure: ProcedureCode) => void;
  onInfo: (procedure: ProcedureCode) => void;
  onDelete: (procedure: ProcedureCode) => void;
  onStatusToggle: (procedure: ProcedureCode, isActive: boolean) => void;
  isUpdating?: boolean;
  readOnly?: boolean;
}

function CustomCodeRow({
  procedure,
  onEdit,
  onInfo,
  onDelete,
  onStatusToggle,
  isUpdating = false,
  readOnly = false,
}: CustomCodeRowProps) {
  const statusCol = getColumnConfig("status");
  const codeCol = getColumnConfig("code");
  const nameCol = getColumnConfig("name");
  const insuranceFeeCol = getColumnConfig("insuranceFee");
  const selfPayFeeCol = getColumnConfig("selfPayFee");
  const actionCol = getColumnConfig("action");

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onStatusToggle(procedure, event.currentTarget.checked);
  };

  return (
    <Table.Tr data-testid={`custom-code-row-${procedure.code}`}>
      <Table.Td colSpan={statusCol.colSpan}>
        <Switch
          checked={procedure.isActive}
          onChange={handleStatusChange}
          disabled={readOnly || isUpdating}
          aria-label={`Toggle ${procedure.code} status`}
          label={procedure.isActive ? "Active" : "Inactive"}
          data-testid={`custom-code-status-toggle-${procedure.code}`}
        />
      </Table.Td>
      <Table.Td colSpan={codeCol.colSpan}>
        <TextWrapper>{procedure.code}</TextWrapper>
      </Table.Td>
      <Table.Td colSpan={nameCol.colSpan}>
        <Group gap="xs">
          <TextWrapper>{procedure.name}</TextWrapper>
          <UnstyledButton
            onClick={() => onInfo(procedure)}
            data-testid={`custom-code-info-button-${procedure.code}`}
            aria-label={`View info for ${procedure.code} - ${procedure.name}`}
          >
            <TextWrapper variant="bold" td="underline">
              Info
            </TextWrapper>
          </UnstyledButton>
        </Group>
      </Table.Td>
      <Table.Td ta={insuranceFeeCol.align} colSpan={insuranceFeeCol.colSpan}>
        <TextWrapper>{formatCurrency(procedure.insuranceFee)}</TextWrapper>
      </Table.Td>
      <Table.Td ta={selfPayFeeCol.align} colSpan={selfPayFeeCol.colSpan}>
        <TextWrapper>{formatCurrency(procedure.selfPayFee)}</TextWrapper>
      </Table.Td>
      <Table.Td ta={actionCol.align} colSpan={actionCol.colSpan}>
        {readOnly ? null : (
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => onDelete(procedure)}
              aria-label={`Delete ${procedure.code}`}
              data-testid={`custom-code-delete-button-${procedure.code}`}
            >
              <IconTrash size={18} />
            </ActionIcon>
            <ActionIcon
              color="gray"
              variant="subtle"
              onClick={() => onEdit(procedure)}
              aria-label={`Edit ${procedure.code} fees`}
              data-testid={`custom-code-edit-button-${procedure.code}`}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
}
